import type Lenis from 'lenis'
import Alpine from 'alpinejs'
import { useElementSize } from '~/scripts/composables/useElementSize'
import setScrollbarWidth from '~/scripts/tailwindcss/plugins/set-scrollbar-width'
import { setRootCSSVariable } from '~/scripts/utils/css'

const ui = {
  init() {
    if (window.lenis) {
      window.lenis.on('scroll', (lenis: Lenis) => {
        this.scrollY = lenis.scroll
      })
    }
    else {
      console.warn(
        'Lenis is not instantiated so everything that uses $store.ui.scrollY won\'t work as expected.',
      )
    }

    // Effect of scrollLockedBy
    Alpine.effect(() => {
      const locked = this.scrollLockedBy.length > 0

      if (window.lenis) {
        window.lenis[locked ? 'stop' : 'start']()
      }
      else {
        const html = document.documentElement
        html.classList[locked ? 'add' : 'remove']('lock-scroll')
      }
    })

    // Effect of headerHeight and wpAdminBarHeight
    Alpine.effect(() => {
      this.offsetTop = this.headerHeight + (this.wpAdminBarFixed ? this.wpAdminBarHeight : 0)
      setRootCSSVariable('offset-top', `${this.offsetTop}px`)
      this.scrollPaddingTop = this.offsetTop + 100
      setRootCSSVariable('scroll-padding-top', `${this.scrollPaddingTop}px`)
    })

    // Observe WP Toolbar height
    const wpAdminBar = document.querySelector<HTMLElement>('#wpadminbar')
    if (wpAdminBar) {
      useElementSize(wpAdminBar, (size) => {
        this.setWpToolbarHeight(size.height)
      })
    }

    // Observe WP Toolbar fixed
    const wpAdminBarFixedMediaQuery = window.matchMedia('(min-width: 601px)')
    this.setWpToolbarFixed(wpAdminBarFixedMediaQuery.matches)
    wpAdminBarFixedMediaQuery.addEventListener('change', (event) => {
      this.setWpToolbarFixed(event.matches)
    })

    Alpine.effect(() => {
      setRootCSSVariable('bg-color', this.bgColor ?? '')
      setRootCSSVariable('color', this.color ?? '')
      setRootCSSVariable('header-color', this.headerColor ?? '')
      setRootCSSVariable('gl-opacity', this.glOpacity?.toString() ?? '')
      setRootCSSVariable('gl-saturate', this.glSaturate?.toString() ?? '')
    })

    setTimeout(() => {
      this.scrollY = window.scrollY
    }, 100)
  },

  // theme: 'page' as Theme,
  bgColor: null as string | null,
  color: null as string | null,
  headerColor: null as string | null,
  glOpacity: null as number | null,
  glSaturate: null as number | null,

  setTheme(value: Theme) {
    if (value === 'page') {
      this.bgColor = null
      this.color = null
      this.headerColor = null
      this.glOpacity = null
      this.glSaturate = null
    }
    else {
      this.bgColor = value === 'light' ? 'var(--color-white)' : 'var(--color-neutral-950)'
      this.color = value === 'light' ? 'var(--color-neutral-950)' : 'var(--color-neutral-50)'
      this.headerColor = value === 'light' ? 'var(--color-blue-800)' : 'var(--color-neutral-50)'
      this.glOpacity = value === 'light' ? 0.2 : 1
      this.glSaturate = value === 'light' ? 0 : 1
    }
  },

  // Scroll
  scrollY: 0,
  get scrolled() {
    return this.scrollY >= 10
  },

  // Lock scroll
  scrollLockedBy: [] as string[],
  lockScrollBy(key: string) {
    this.scrollLockedBy.push(key)
    setTimeout(() => {
      setScrollbarWidth()
    }, 100)
  },
  unlockScrollBy(key: string) {
    this.scrollLockedBy = this.scrollLockedBy.filter(item => item !== key)
    setTimeout(() => {
      setScrollbarWidth()
    }, 100)
  },

  // Header
  topbarHeight: 0,
  navbarHeight: 0,
  menuOffsetTop: 0,
  get headerHeight() {
    return this.topbarHeight + this.navbarHeight
  },
  setTopbarHeight(value: number) {
    this.topbarHeight = value
    setRootCSSVariable('topbar-height', `${value}px`)
  },
  setNavbarHeight(value: number) {
    this.navbarHeight = value
    setRootCSSVariable('navbar-height', `${value}px`)
  },
  setMenuOffsetTop(value: number) {
    this.menuOffsetTop = value
    setRootCSSVariable('menu-offset-top', `${value}px`)
  },

  // WP Toolbar
  wpAdminBarFixed: false,
  wpAdminBarHeight: 0,
  setWpToolbarHeight(value: number) {
    this.wpAdminBarHeight = value
    setRootCSSVariable('wp-admin-bar-height', `${value}px`)
  },
  setWpToolbarFixed(value: boolean) {
    this.wpAdminBarFixed = value
    setRootCSSVariable('wp-admin-bar-fixed', value ? '1' : '0')
  },

  // Offset
  offsetTop: 0,
  scrollPaddingTop: 0,

  // Menu
  menuVisible: false,
  setMenuVisible(value: boolean) {
    if (this.menuVisible !== value) {
      this.menuVisible = value
      if (value) {
        this.lockScrollBy('menu')
      }
      else {
        this.unlockScrollBy('menu')
      }
    }
  },
  toggleMenu() {
    this.setMenuVisible(!this.menuVisible)
  },
}

// Declare uiStore as an Alpine store module
declare module 'alpinejs' {
  interface Stores {
    ui: typeof ui
  }
}

export default ui
